import { createReducer, createActions } from "reduxsauce";
import { MapSchema } from "schemas/SharedSchemas";

import get from "lodash/get";

const toGeoJSONInput = (geoJSON) => {
  if (!get(geoJSON, "data", null)) return;
  return geoJSON;
};

/**
 * ------------- Types and Action Creators -------------
 * The keys of the object will become keys of the Creators.
 * They will also become the keys of the Types after being
 * converted to SCREAMING_SNAKE_CASE.
 */
const { Types, Creators } = createActions({
  fetchFarm: ["id"],
  fetchFarmSuccess: ["data"],
  fetchFarmFailure: ["data"],
  createFarm: ["data"],
  createFarmSuccess: null,
  createFarmFailure: null,
  setFarmMapData: ["mapKey", "data"],
  updateFarmMapData: ["index", "name", "mapKey"],
  setFarmLocation: ["location"],
  deleteFarmMapData: ["mapKey"],
  requestSoilData: ["queryParams", "callback"],
  requestSoilDataSuccess: ["data"],
  requestSoilDataFailure: ["message"],
  fetchParcelCLU: ["callback"],
  fetchParcelCLUCompleted: ["data"],
  updateFarmAddress: ["id", "data", "callback"],
  updateFarmAddressSuccess: ["data"],
  updateFarmAddressFailure: null,
  updateFarm: ["id", "data", "callback"],
  updateFarmSuccess: ["data"],
  updateFarmFailure: null,
  clearFarmData: null,
  fetchFarmOverview: ["id"],
  fetchFarmOverviewSuccess: ["data"],
  fetchFarmOverviewFailure: ["data"],
  updateFarmAerialMap: ["farmFields"],
});

export const FarmTypes = Types;
export default Creators;

/* ------------- Initial State ------------- */

export const INITIAL_STATE = {
  name: null,
  description: null,
  stateId: null,
  cityId: null,
  lat: null,
  lng: null,
  aerialMap: {},
  aerialFeatures: [],
  soilMap: {},
  soilFeatures: [],
  soilCodes: [],
  location: {
    address: null,
    zipCode: null,
    city: null,
    county: null,
    state: null,
  },
  landValues: {
    tillableAcres: null,
    totalAcres: null,
    soilIndex: null,
    indexType: null,
    avgPrice: 0,
    lowPrice: 0,
    highPrice: 0,
  },
  isFetchingCLUs: false,
  isPopulatingSoilData: false,
  isUpdating: false,
  isCreatingFarm: false,
  isSupportedState: true, // TODO: deprecate this in favor of backend services
  isInitialState: true,
};

/* ------------- Selectors ------------- */

export const FarmSelectors = {
  farm: (state) => ({
    id: state.farm.id,
    address: state.farm.location.address,
    zipCode: state.farm.location.zipCode,
    name: state.farm.name,
    description: state.farm.description,
    lat: state.farm.lat,
    lng: state.farm.lng,
    stateId: state.farm.stateId,
    cityId: state.farm.cityId,
    aerialMap: toGeoJSONInput(state.farm.aerialMap),
    aerialFeatures: state.farm.aerialFeatures,
    soilMap: toGeoJSONInput(state.farm.soilMap),
    soilSummary: state.farm.soilSummary,
    soilFeatures: state.farm.soilFeatures,
    soilCodes: state.farm.soilCodes,
    isGettingFarm: state.farm.isGettingFarm,
    legalEntityId: state.farm.legalEntityId,
    landConnection: state.farm.landConnection,
    user: state.farm.user,
    owner: state.farm.owner,
    city: state.farm.city,
    fields: state.farm.fields,
    estimate: state.farm.estimate,
    landValues: state.farm.landValues,
    location: state.farm.location,
  }),
  landValues: (state) => state.farm.landValues,
  landLocation: (state) => state.farm.location,
  isSupportedState: (state) => state.farm.isSupportedState,
  isInitialState: (state) => state.farm.isInitialState,
};

/* ------------- Reducers ------------- */

export const fetchFarm = (state = INITIAL_STATE) => ({
  ...state,
  isGettingFarm: true,
});

export const fetchFarmSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;
  const newState = {
    ...state,
    aerialMap: data.aerialMap,
    soilMap: data.soilMap,
    location: {
      address: data.address,
      zipCode: data.zipCode,
      city: data.city,
    },
    ...data,
    isGettingFarm: false,
    isInitialState: false,
  };
  return newState;
};

export const fetchFarmFailure = (state = INITIAL_STATE) => ({
  ...state,
  isGettingFarm: false,
});

export const createFarm = (state = INITIAL_STATE) => ({
  ...state,
  isCreatingFarm: true,
});

export const createFarmSuccess = (state = INITIAL_STATE) => ({
  ...state,
  isCreatingFarm: false,
});

export const createFarmFailure = (state = INITIAL_STATE) => ({
  ...state,
  isCreatingFarm: false,
});

export const setFarmLocation = (state = INITIAL_STATE, action) => {
  const { location } = action;
  const { stateId, cityId, lat, lng, ...rest } = location;

  const newState = {
    ...state,
    stateId,
    cityId,
  };

  if (lat) newState.lat = lat;
  if (lng) newState.lng = lng;
  if (rest) {
    newState.location = {
      ...state.location,
      ...rest,
    };
  }

  return newState;
};

export const setFarmMapData = (state = INITIAL_STATE, action) => {
  const { mapKey, data } = action;
  const currentMapData = state[mapKey];
  const newState = {
    ...state,
    isInitialState: false,
    [mapKey]: {
      ...currentMapData,
      ...MapSchema.clean(data),
    },
  };

  if (mapKey === "aerialMap") {
    if (currentMapData && currentMapData.viewport) {
      const { center } = currentMapData.viewport;

      newState.lng = center[1];
      newState.lat = center[0];
    }

    // reset aerial map to avoid polygon inconsistencies
    newState.soilMap = {};
  }

  return newState;
};

export const deleteFarmMapData = (state = INITIAL_STATE, action) => {
  const { mapKey } = action;

  return {
    ...state,
    [mapKey]: undefined,
  };
};

export const requestSoilData = (state = INITIAL_STATE, action) => ({
  ...state,
  lat: action.queryParams.lat,
  lng: action.queryParams.lng,
  isPopulatingSoilData: true,
});

export const fetchParcelCLU = (state = INITIAL_STATE) => ({
  ...state,
  isFetchingCLUs: true,
});

export const fetchParcelCLUCompleted = (state = INITIAL_STATE, action) => ({
  ...state,
  aerialFeatures: get(action, "data.cluFeatures", []),
  isFetchingCLUs: false,
});

export const requestSoilDataSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;
  const {
    soilData,
    location,
  } = data;
  // Handle unsupported states as soil data won't be available
  const indexType = get(soilData, "indexType", "NCCPI");
  const soilIndex = get(soilData, "soilIndex", 0);
  const soilSummary = get(soilData, "soilSummary", []);
  const soilFeatures = get(soilData, "soilFeatures", []);
  const soilCodes = get(soilData, "detailInfoList", []);
  const tillableAcres = get(soilData, "tillableAcres", 0);
  const newState = {
    ...state,
    soilSummary,
    soilFeatures,
    soilCodes,
    landValues: {
      ...state.landValues,
      tillableAcres,
      totalAcres: state.aerialMap.layersInfo.totalAcres,
      soilIndex: Math.round(soilIndex * 100) / 100,
      indexType,
    },
    location,
    isPopulatingSoilData: false,
  };

  return newState;
};

export const requestSoilDataFailure = (state = INITIAL_STATE) => ({
  ...state,
  isPopulatingSoilData: false,
});

export const updateFarmAddress = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: true,
});

export const updateFarmAddressSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;

  return {
    ...state,
    stateId: data.stateId,
    cityId: data.cityId,
    location: {
      ...state.location,
      address: data.address,
      zipcode: data.zipcode,
    },
    lat: data.lat,
    lng: data.lng,
    isUpdating: false,
  };
};

export const updateFarmAddressFailure = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: false,
});

export const updateFarm = (state = INITIAL_STATE, action) => {
  const { data } = action;

  return {
    ...state,
    stateId: data.stateId,
    cityId: data.cityId,
    location: {
      ...state.location,
      address: data.address,
      zipcode: data.zipcode,
    },
    isUpdating: true,
  };
};

export const updateFarmSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;
  return {
    ...state,
    name: data.name,
    isUpdating: false,
  };
};

export const updateFarmFailure = (state = INITIAL_STATE) => ({
  ...state,
  isUpdating: false,
});

export const updateFarmMapData = (state = INITIAL_STATE, action) => {
  const { index, name, mapKey } = action;
  const data = state[mapKey].data.map((object, i) => {
    if (i === index) {
      object.properties.name = name;
      return object;
    }
    return object;
  });
  return {
    ...state,
    [mapKey]: {
      ...state[mapKey],
      data,
    },
  };
};

export const fetchFarmOverview = (state = INITIAL_STATE) => ({
  ...state,
  isGettingFarm: true,
});

export const fetchFarmOverviewSuccess = (state = INITIAL_STATE, action) => {
  const { data } = action;
  const newState = {
    ...state,
    ...data,
    isGettingFarm: false,
    isInitialState: false,
  };
  return newState;
};

export const fetchFarmOverviewFailure = (state = INITIAL_STATE) => ({
  ...state,
  isGettingFarm: false,
});

export const updateFarmAerialMap = (state = INITIAL_STATE, action) => {
  const { farmFields } = action;
  return {
    ...state,
    aerialMap: {
      ...state.aerialMap,
      data: state.aerialMap.data.filter((field) => farmFields.indexOf(field.properties.id) >= 0),
    },
    soilMap: {
      ...state.soilMap,
      data: state.soilMap.data.filter((soil) => farmFields.indexOf(soil.properties.parentId) >= 0),
    },
  };
};

export const clearFarmData = () => INITIAL_STATE;


/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.FETCH_FARM]: fetchFarm,
  [Types.FETCH_FARM_SUCCESS]: fetchFarmSuccess,
  [Types.FETCH_FARM_FAILURE]: fetchFarmFailure,
  [Types.CREATE_FARM]: createFarm,
  [Types.CREATE_FARM_SUCCESS]: createFarmSuccess,
  [Types.CREATE_FARM_FAILURE]: createFarmFailure,
  [Types.SET_FARM_MAP_DATA]: setFarmMapData,
  [Types.UPDATE_FARM_MAP_DATA]: updateFarmMapData,
  [Types.SET_FARM_LOCATION]: setFarmLocation,
  [Types.DELETE_FARM_MAP_DATA]: deleteFarmMapData,
  [Types.REQUEST_SOIL_DATA]: requestSoilData,
  [Types.REQUEST_SOIL_DATA_SUCCESS]: requestSoilDataSuccess,
  [Types.REQUEST_SOIL_DATA_FAILURE]: requestSoilDataFailure,
  [Types.FETCH_PARCEL_CLU]: fetchParcelCLU,
  [Types.FETCH_PARCEL_CLU_COMPLETED]: fetchParcelCLUCompleted,
  [Types.UPDATE_FARM_ADDRESS]: updateFarmAddress,
  [Types.UPDATE_FARM_ADDRESS_SUCCESS]: updateFarmAddressSuccess,
  [Types.UPDATE_FARM_ADDRESS_FAILURE]: updateFarmAddressFailure,
  [Types.UPDATE_FARM]: updateFarm,
  [Types.UPDATE_FARM_SUCCESS]: updateFarmSuccess,
  [Types.UPDATE_FARM_FAILURE]: updateFarmFailure,
  [Types.FETCH_FARM_OVERVIEW]: fetchFarmOverview,
  [Types.FETCH_FARM_OVERVIEW_SUCCESS]: fetchFarmOverviewSuccess,
  [Types.FETCH_FARM_OVERVIEW_FAILURE]: fetchFarmOverviewFailure,
  [Types.UPDATE_FARM_AERIAL_MAP]: updateFarmAerialMap,
  [Types.CLEAR_FARM_DATA]: clearFarmData,
});
